
import React, { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const PartnersPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const partnerCategories = [
    { id: 'all', name: 'All Partners' },
    { id: 'technology', name: 'Technology' },
    { id: 'integration', name: 'Integration' },
    { id: 'real-estate', name: 'Real Estate' },
    { id: 'marketing', name: 'Marketing' }
  ];

  const partners = [
    {
      id: 1,
      name: 'Zillow Group',
      category: 'real-estate',
      logo: 'https://public.readdy.ai/ai/img_res/8dea4fc6092f17afa972a79d7c632876.jpg',
      description: 'Leading online marketplace for real estate, providing comprehensive listing data and market insights.',
      benefits: ['Direct MLS integration', 'Real-time market data', 'Property valuation tools'],
      tier: 'Strategic'
    },
    {
      id: 2,
      name: 'Salesforce',
      category: 'technology',
      logo: 'https://readdy.ai/api/search-image?query=salesforce%20cloud%20technology%20company%20logo%20design%20with%20modern%20blue%20background%20professional%20corporate%20style%20clean%20minimal&width=200&height=100&seq=salesforce-logo&orientation=landscape',
      description: 'World\'s leading CRM platform, enabling seamless client relationship management and automation.',
      benefits: ['CRM integration', 'Lead management', 'Workflow automation'],
      tier: 'Strategic'
    },
    {
      id: 3,
      name: 'DocuSign',
      category: 'integration',
      logo: 'https://readdy.ai/api/search-image?query=docusign%20digital%20signature%20company%20logo%20design%20with%20professional%20blue%20background%20modern%20corporate%20branding%20style&width=200&height=100&seq=docusign-logo&orientation=landscape',
      description: 'Digital transaction management platform for secure electronic signatures and document workflows.',
      benefits: ['E-signature integration', 'Document automation', 'Compliance tracking'],
      tier: 'Technology'
    },
    {
      id: 4,
      name: 'HubSpot',
      category: 'marketing',
      logo: 'https://readdy.ai/api/search-image?query=hubspot%20marketing%20automation%20company%20logo%20design%20with%20clean%20blue%20background%20professional%20corporate%20style%20modern%20branding&width=200&height=100&seq=hubspot-logo&orientation=landscape',
      description: 'Inbound marketing and sales platform for lead generation and customer relationship management.',
      benefits: ['Marketing automation', 'Lead scoring', 'Email campaigns'],
      tier: 'Technology'
    },
    {
      id: 5,
      name: 'Matterport',
      category: 'technology',
      logo: 'https://readdy.ai/api/search-image?query=matterport%203d%20virtual%20tour%20technology%20company%20logo%20design%20with%20modern%20blue%20background%20professional%20corporate%20branding%20style&width=200&height=100&seq=matterport-logo&orientation=landscape',
      description: '3D virtual tour technology for immersive property showcasing and digital twin creation.',
      benefits: ['3D virtual tours', 'Property measurements', 'Virtual staging'],
      tier: 'Technology'
    },
    {
      id: 6,
      name: 'Zapier',
      category: 'integration',
      logo: 'https://readdy.ai/api/search-image?query=zapier%20automation%20platform%20company%20logo%20design%20with%20clean%20blue%20background%20modern%20corporate%20style%20professional%20branding&width=200&height=100&seq=zapier-logo&orientation=landscape',
      description: 'Automation platform connecting apps and services for streamlined workflow management.',
      benefits: ['App integrations', 'Workflow automation', 'Data synchronization'],
      tier: 'Integration'
    },
    {
      id: 7,
      name: 'Realtor.com',
      category: 'real-estate',
      logo: 'https://readdy.ai/api/search-image?query=realtor.com%20real%20estate%20platform%20company%20logo%20design%20with%20professional%20blue%20background%20modern%20corporate%20branding%20style&width=200&height=100&seq=realtor-logo&orientation=landscape',
      description: 'Premier real estate website providing comprehensive property listings and market data.',
      benefits: ['Listing syndication', 'Market analytics', 'Lead generation'],
      tier: 'Strategic'
    },
    {
      id: 8,
      name: 'Google Cloud',
      category: 'technology',
      logo: 'https://readdy.ai/api/search-image?query=google%20cloud%20platform%20technology%20company%20logo%20design%20with%20clean%20blue%20background%20professional%20corporate%20style%20modern%20branding&width=200&height=100&seq=google-cloud-logo&orientation=landscape',
      description: 'Cloud computing platform providing AI/ML services and scalable infrastructure solutions.',
      benefits: ['AI/ML services', 'Cloud infrastructure', 'Data analytics'],
      tier: 'Strategic'
    }
  ];

  const filteredPartners = selectedCategory === 'all' 
    ? partners 
    : partners.filter(partner => partner.category === selectedCategory);

  const partnershipBenefits = [
    {
      icon: 'ri-rocket-line',
      title: 'Accelerated Growth',
      description: 'Leverage our AI technology to expand your market reach and increase revenue opportunities.'
    },
    {
      icon: 'ri-team-line',
      title: 'Joint Innovation',
      description: 'Collaborate on cutting-edge solutions that transform the real estate industry together.'
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Dedicated Support',
      description: 'Access our technical expertise and dedicated partnership management team.'
    },
    {
      icon: 'ri-global-line',
      title: 'Market Expansion',
      description: 'Tap into new markets and customer segments through our extensive network.'
    }
  ];

  const partnershipTiers = [
    {
      name: 'Technology Partner',
      price: 'Custom',
      features: [
        'API access and integration',
        'Technical documentation',
        'Developer support',
        'Co-marketing opportunities',
        'Revenue sharing model'
      ],
      popular: false
    },
    {
      name: 'Strategic Partner',
      price: 'Enterprise',
      features: [
        'Deep platform integration',
        'Joint product development',
        'Dedicated account manager',
        'Priority technical support',
        'Executive relationship program',
        'Custom partnership terms'
      ],
      popular: true
    },
    {
      name: 'Integration Partner',
      price: 'Flexible',
      features: [
        'Pre-built integrations',
        'Marketplace listing',
        'Technical certification',
        'Marketing support',
        'Lead sharing program'
      ],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">
              Partner with AI Consulting Authority
            </h1>
            <p className="text-xl mb-8 text-blue-100">
              Join our ecosystem of innovative companies transforming real estate through AI technology. 
              Together, we're building the future of property transactions and client experiences.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer">
                Become a Partner
              </button>
              <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
                View Partnership Guide
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Partner with Us?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our partnership program offers unique opportunities to grow your business while delivering 
              exceptional value to real estate professionals worldwide.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {partnershipBenefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${benefit.icon} text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Partners */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Our Trusted Partners
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              We collaborate with industry leaders to deliver comprehensive AI solutions 
              that transform real estate operations and client experiences.
            </p>
            
            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {partnerCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    selectedCategory === category.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredPartners.map((partner) => (
              <div key={partner.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <img 
                    src={partner.logo} 
                    alt={`${partner.name} logo`}
                    className="h-12 w-auto object-contain"
                  />
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    partner.tier === 'Strategic' 
                      ? 'bg-blue-100 text-blue-800'
                      : partner.tier === 'Technology'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-purple-100 text-purple-800'
                  }`}>
                    {partner.tier}
                  </span>
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{partner.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{partner.description}</p>
                
                <div className="space-y-2">
                  {partner.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center text-sm text-gray-700">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      {benefit}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Tiers */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Partnership Tiers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the partnership level that best fits your business goals and integration needs.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {partnershipTiers.map((tier, index) => (
              <div key={index} className={`bg-white rounded-xl shadow-sm p-8 ${
                tier.popular ? 'ring-2 ring-blue-500 relative' : ''
              }`}>
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                  <div className="text-3xl font-bold text-blue-600 mb-4">{tier.price}</div>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <i className="ri-check-line text-green-500 mr-3 mt-0.5"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  tier.popular
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Partnership Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how our partners have achieved remarkable growth and innovation through collaboration.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-xl p-8">
              <div className="flex items-center mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20real%20estate%20technology%20company%20logo%20design%20with%20modern%20blue%20background%20corporate%20branding%20style%20clean%20minimal&width=60&height=60&seq=partner-success-1&orientation=squarish"
                  alt="Partner logo"
                  className="w-12 h-12 rounded-lg mr-4"
                />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">PropTech Solutions</h3>
                  <p className="text-gray-600">Technology Partner</p>
                </div>
              </div>
              
              <blockquote className="text-gray-700 mb-6">
                "Partnering with AI Consulting Authority has transformed our platform capabilities. 
                We've seen a 300% increase in user engagement and 150% growth in revenue since integration."
              </blockquote>
              
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-600">300%</div>
                  <div className="text-sm text-gray-600">User Engagement</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">150%</div>
                  <div className="text-sm text-gray-600">Revenue Growth</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">50K+</div>
                  <div className="text-sm text-gray-600">Active Users</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-8">
              <div className="flex items-center mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20real%20estate%20brokerage%20company%20logo%20design%20with%20professional%20blue%20background%20corporate%20style%20clean%20branding&width=60&height=60&seq=partner-success-2&orientation=squarish"
                  alt="Partner logo"
                  className="w-12 h-12 rounded-lg mr-4"
                />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Elite Realty Group</h3>
                  <p className="text-gray-600">Strategic Partner</p>
                </div>
              </div>
              
              <blockquote className="text-gray-700 mb-6">
                "The AI tools have revolutionized our operations. Our agents are closing deals 40% faster 
                and our client satisfaction scores have reached an all-time high of 98%."
              </blockquote>
              
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-600">40%</div>
                  <div className="text-sm text-gray-600">Faster Closings</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">98%</div>
                  <div className="text-sm text-gray-600">Client Satisfaction</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">$2.5M</div>
                  <div className="text-sm text-gray-600">Monthly Sales</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Transform Real Estate Together?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Join our growing network of partners and unlock new opportunities for growth, 
            innovation, and success in the real estate industry.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer">
              Apply for Partnership
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              Schedule a Call
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PartnersPage;
